import { memo, ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  getSelectsOptions,
  rejectionError,
  SelectsOptions,
  notification,
  ValidationError,
} from '@ohpen/enso-ui';
import { crudToActionMap, changeRequestButtonMessage } from '@pmp/utils';
import { ActionEntityType, DraftNote, notesCreateNote, notesUpdateNote } from '@pmp/api';
import { useFeatureToggle } from '@pmp/hooks';

import { NotesCrudChangeResponse, NotesCrudFormProps } from '../interfaces';
import { getFormSchema } from '../formConfig';

export const NotesCrudForm = memo(
  ({ mode, record, valueLists, refreshTable, onClose, entityType, entityId, userName }: NotesCrudFormProps): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const onConfirm: (formData: DraftNote) => Promise<ValidationError[] | void> = useCallback(
      values => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const body: DraftNote = {
          ...values,
          entityType,
          entityId,
          user: record?.user || userName,
        };

        if (mode === CrudMode.CREATE) {
          return notesCreateNote({ body }).then((data: NotesCrudChangeResponse) => {
            refreshTable();
            notification().success({
              title: t('global.notifications.xCreatedSuccessfully', { x: t('global.labels.note') }),
            });
          });
        }

        if (mode === CrudMode.EDIT) {
          if (!record?.id) return Promise.reject(rejectionError('Invalid data', { record }));
          return notesUpdateNote({ body, path: { noteId: record.id } }).then((data: NotesCrudChangeResponse) => {
            refreshTable();
            notification().success({
              title: t('global.notifications.xEditedSuccessfully', { x: t('global.labels.note') }),
            });
          });
        }

        return Promise.reject(rejectionError('No mode provided', record));
      },
      [mode, refreshTable, t, record, userName, entityId, entityType],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('global.labels.note') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('global.labels.note') }),
      };
      return titles[mode];
    }, [mode, t]);

    const initialValues = useMemo(() => {
      return {
        entityId: record?.entityId,
        entityType: record?.entityType as unknown as ActionEntityType,
        text: record?.text,
        category: record?.category,
        user: record?.user || userName,
      } as DraftNote;
    }, [record, userName]);

    const crudProps: CrudProps<DraftNote> = useMemo(() => {
      const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

      return {
        ref,
        open,
        schema: getFormSchema(selectsOptions, mode),
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : t('global.labels.create'),
        },
        initialValues,
        mode,
        onClose: handleOnClose,
        onConfirm,
      };
    }, [valueLists, open, mode, title, t, handleOnClose, onConfirm, initialValues]);

    return <Crud {...crudProps} />;
  },
);
